"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import type { DateTime } from "effect";
import { CreditCard, ChevronDown, ChevronUp } from "lucide-react";
import { StatusBanner, StatusBannerRow, StatusBannerContent, StatusBannerActions, StatusBannerIconButton, type StatusBannerColor, STATUS_BANNER_PRESS, statusBannerColorMap } from "@/components/ui/status-banner";
import { formatBillingDate } from "./billing-helpers";
import { AddPaymentMethodModal } from "./add-payment-method-modal";
interface PaymentMethodSetupBannerProps {
  className?: string;
  /** Next renewal date (= billing_accounts.current_period_end); null if unknown. */
  renewalAt: DateTime.Utc | null;
  /** Org-admin role (includes super-admins). Drives the add-card CTA vs member copy. */
  isAdmin: boolean;
  /** Billing-admin display name for member copy; falls back to "your organisation admin". */
  billingAdminName: string | null;
}
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * App-wide, collapsible (not dismissible) banner shown when the org is on
 * automatic card billing with a live subscription but NO card on file — the
 * gap a manual→auto billing-type flip opens. It drives the customer to add a
 * card BEFORE the renewal date, so the next auto-charge doesn't silently fail
 * into dunning.
 *
 * Org-admins get an in-place "Add payment method" CTA. Everyone else gets a
 * gentle "please let [admin] know" prompt — no card form, no lockout notify
 * (that path sends a payment-failed email).
 *
 * Informational brand purple by default; escalates to amber when the renewal is
 * imminent (<= 3 days). Red is deliberately NOT used — it's reserved for the
 * soft/hard-lock banners, and this is a pre-emptive nudge, not a lockout.
 */
export function PaymentMethodSetupBanner(t0) {
  const $ = _c(22);
  const {
    className,
    renewalAt,
    isAdmin,
    billingAdminName
  } = t0;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [nowMs] = useState(_temp);
  let color;
  let renewalLabel;
  let t1;
  if ($[0] !== billingAdminName || $[1] !== isAdmin || $[2] !== nowMs || $[3] !== renewalAt) {
    renewalLabel = renewalAt ? formatBillingDate(renewalAt) : null;
    const daysUntil = renewalAt ? Math.max(0, Math.ceil((renewalAt.epochMillis - nowMs) / DAY_MS)) : null;
    color = daysUntil !== null && daysUntil <= 3 ? "amber" : "purple";
    t1 = getCopy({
      isAdmin,
      billingAdminName,
      renewalLabel,
      daysUntil
    });
    $[0] = billingAdminName;
    $[1] = isAdmin;
    $[2] = nowMs;
    $[3] = renewalAt;
    $[4] = color;
    $[5] = renewalLabel;
    $[6] = t1;
  } else {
    color = $[4];
    renewalLabel = $[5];
    t1 = $[6];
  }
  const copy = t1;
  let t2;
  if ($[7] !== className || $[8] !== color || $[9] !== copy.body || $[10] !== copy.summary || $[11] !== copy.title || $[12] !== isAdmin || $[13] !== isCollapsed) {
    t2 = isCollapsed ? <StatusBanner color={color} className={className}><button type="button" onClick={() => setIsCollapsed(false)} className={`flex w-full cursor-pointer items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors ${STATUS_BANNER_PRESS}`}><CreditCard className="h-3.5 w-3.5 shrink-0" /><span>{copy.summary}</span><ChevronDown className="ml-1 h-3.5 w-3.5" /></button></StatusBanner> : <StatusBanner color={color} className={className}><StatusBannerRow className="items-start"><StatusBannerContent className="flex items-start gap-3"><CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-white/80" /><div className="flex-1"><p className="text-sm font-medium text-white">{copy.title}</p><p className="mt-0.5 text-xs">{copy.body}</p>{isAdmin ? <button type="button" onClick={() => setModalOpen(true)} className={`mt-2 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors active:scale-[0.97] ${statusBannerColorMap[color].actionButton}`}><CreditCard className="h-3.5 w-3.5" />Add payment method</button> : null}</div></StatusBannerContent><StatusBannerActions><StatusBannerIconButton color={color} onClick={() => setIsCollapsed(true)} aria-label="Collapse banner"><ChevronUp className="h-4 w-4" /></StatusBannerIconButton></StatusBannerActions></StatusBannerRow></StatusBanner>;
    $[7] = className;
    $[8] = color;
    $[9] = copy.body;
    $[10] = copy.summary;
    $[11] = copy.title;
    $[12] = isAdmin;
    $[13] = isCollapsed;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== isAdmin || $[16] !== modalOpen || $[17] !== renewalLabel) {
    t3 = isAdmin ? <AddPaymentMethodModal open={modalOpen} onOpenChange={setModalOpen} renewalLabel={renewalLabel} /> : null;
    $[15] = isAdmin;
    $[16] = modalOpen;
    $[17] = renewalLabel;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t2 || $[20] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
}
function _temp() {
  return Date.now();
}
function getCopy(args: {
  isAdmin: boolean;
  billingAdminName: string | null;
  renewalLabel: string | null;
  daysUntil: number | null;
}): {
  title: string;
  body: string;
  summary: string;
} {
  if (args.isAdmin) {
    const urgencyLine = args.daysUntil !== null ? args.daysUntil === 0 ? "Your subscription renews today — add a card now to avoid interruption." : `Add one in the next ${args.daysUntil} ${args.daysUntil === 1 ? "day" : "days"} to avoid interruption.` : "Add one before your next renewal to avoid interruption.";
    return {
      title: args.renewalLabel ? `Add a payment method before ${args.renewalLabel}` : "Add a payment method to keep your service active",
      body: `Your billing is set to automatic card payment, but there's no card on file. ${urgencyLine}`,
      summary: args.renewalLabel ? `Add a payment method before ${args.renewalLabel} to keep your service active` : "Add a payment method to keep your service active at renewal"
    };
  }
  const adminLabel = args.billingAdminName ?? "your organisation admin";
  const beforeRenewal = args.renewalLabel ? ` before ${args.renewalLabel}` : " before the next renewal";
  return {
    title: args.renewalLabel ? `A payment method is needed before ${args.renewalLabel}` : "A payment method is needed for this organisation",
    body: `Billing is set to automatic, but a card hasn't been added yet. Please let ${adminLabel} know so they can add one${beforeRenewal}. Nothing is interrupted right now.`,
    summary: `Please let ${adminLabel} know a payment method is needed${beforeRenewal}`
  };
}